import React, { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import {
  buildCatalogPath,
  getCatalogVendeur,
  isCatalogPath,
} from "../../catalog/catalogTenant";
import type { Product } from "../../utils/api/product/product.type";
import {
  useInfiniteSearchProductList,
  useInfiniteScrollSentinel,
} from "../../utils/hooks";
import { QueryUrl, matchesProductTitleSearch } from "../../utils/Utils";
import { BestSellerCard } from "../HomePage/BestSellerCard";
import TemplateNavbar from "../shared/TemplateNavbar/TemplateNavbar";
import ProductCard from "../TemplateCard/ProductCard";
import FilterItems from "../user/ParcoursAchat/FilterItems";
import FilterMobileItem from "../user/ParcoursAchat/modal/FilterMobileItem";
import { ProductItemsSkeleton } from "../user/ParcoursAchat/ParcoursAchat";

const LIMIT = 24;

const CatalogProductSkeleton = () => (
  <>
    {Array(6)
      .fill(null)
      .map((_, i) => (
        <div key={i} className="product-card">
          <div className="product-card__image-wrapper">
            <Skeleton className="product-card__image" height={160} />
          </div>
          <div className="product-card__body">
            <Skeleton width="70%" height={14} style={{ marginBottom: 6 }} />
            <Skeleton width="50%" height={14} style={{ marginBottom: 10 }} />
            <Skeleton width="100%" height={32} />
          </div>
        </div>
      ))}
  </>
);

function SearchResultsPage() {
  const navigate = useNavigate();
  const isCatalog = isCatalogPath();
  const catalogVendeur = getCatalogVendeur();
  const [searchParams, setSearchParams] = useSearchParams();
  const [show, setShow] = useState(false);
  const [page, setPage] = useState(1);

  const q = (searchParams.get("q") || "").trim();

  const [query, setQuery] = useState({
    categorie: searchParams?.get("categorie") || "",
    sous_categorie: searchParams?.get("sous_categorie") || "",
    prix_min: searchParams?.get("prix_min") || "",
    prix_max: searchParams?.get("prix_max") || "",
    orderBy: searchParams?.get("orderBy") || "",
    q,
  });

  React.useEffect(() => {
    setQuery((prev) => ({ ...prev, q }));
  }, [q]);

  const filtersKey =
    (searchParams?.get("categorie") ?? "") +
    "|" +
    (searchParams?.get("sous_categorie") ?? "") +
    "|" +
    (searchParams?.get("prix_min") ?? "") +
    "|" +
    (searchParams?.get("prix_max") ?? "") +
    "|" +
    q;

  const queryArgs = useMemo(
    () => ({
      limit: LIMIT,
      q,
      prix_min: searchParams?.get("prix_min") || "",
      prix_max: searchParams?.get("prix_max") || "",
      categorie: searchParams?.get("categorie") || "",
      sous_categorie: (() => {
        const raw = searchParams?.get("sous_categorie");
        if (!raw) return "";
        try {
          return JSON.parse(raw);
        } catch {
          return "";
        }
      })(),
    }),
    [
      q,
      searchParams?.get("prix_min"),
      searchParams?.get("prix_max"),
      searchParams?.get("categorie"),
      searchParams?.get("sous_categorie"),
    ],
  );

  const {
    displayProducts: rawProducts,
    totalCount: rawTotalCount,
    hasMore,
    isFetching,
    isFetchingMore,
    isLoading,
    loadMore,
  } = useInfiniteSearchProductList(queryArgs, filtersKey);

  const allProducts = useMemo(
    () => rawProducts.filter((item) => matchesProductTitleSearch(item, q)),
    [rawProducts, q],
  );
  // Si le filtre titre côté client retire des résultats, ne pas afficher le count API
  // (souvent plus large en prod si la recherche backend inclut encore description/catégories).
  const clientFilterTightened =
    !!q.trim() && allProducts.length < rawProducts.length;
  const totalCount = clientFilterTightened
    ? allProducts.length
    : rawTotalCount;

  const sentinelRef = useInfiniteScrollSentinel(
    loadMore,
    hasMore,
    allProducts.length,
  );

  const priceExtremes = useMemo(() => {
    if (!allProducts.length) return null;
    let min = Number.POSITIVE_INFINITY;
    let max = 0;
    for (const p of allProducts) {
      const raw = (p as any)?.prix ?? (p as any)?.price ?? null;
      const price = raw != null ? Number(raw) : NaN;
      if (!Number.isFinite(price)) continue;
      if (price < min) min = price;
      if (price > max) max = price;
    }
    if (!Number.isFinite(min)) return null;
    return { min, max };
  }, [allProducts]);

  const handleCatalogCardClick = (product: Product) => {
    navigate(buildCatalogPath(`/produit/${product?.slug}`));
  };

  // Catalogue : retour accueil (catégories déjà présentes), pas la page /produits
  const allProductsLink = isCatalog ? buildCatalogPath("") : "/produits";

  const backToAllProductsLink = (searchParams.get("categorie") || q) && (
    <div className="d-flex justify-content-center mt-2 mb-3">
      <Link
        to={allProductsLink}
        className="btn btn-purple btn-sm d-flex align-items-center gap-2 px-3 py-2 mt-3"
        onClick={() => {
          setSearchParams(
            QueryUrl("", {
              ...query,
              categorie: "",
              sous_categorie: [],
              q: "",
            }),
          );
          setPage(1);
        }}
      >
        <i className="bi bi-arrow-left" />
        Tous les produits
      </Link>
    </div>
  );

  const resultsMeta =
    !isLoading && allProducts.length > 0 ? (
      <p className="search-results-count text-center mb-2">
        {allProducts.length}
        {totalCount > allProducts.length ? ` / ${totalCount}` : ""} produit
        {totalCount > 1 ? "s" : ""}
      </p>
    ) : null;

  const infiniteScrollBlock = (
    <>
      <div
        ref={sentinelRef}
        className="infinite-scroll-sentinel"
        aria-hidden="true"
      />
      {(isFetchingMore || (isFetching && allProducts.length > 0 && hasMore)) && (
        <div className="d-flex justify-content-center py-3">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Chargement...</span>
          </div>
        </div>
      )}
      {hasMore && !isFetching && (
        <div className="d-flex justify-content-center py-2">
          <button
            type="button"
            className="btn btn-outline-primary btn-sm"
            onClick={() => void loadMore()}
          >
            Voir plus de résultats
          </button>
        </div>
      )}
    </>
  );

  if (isCatalog) {
    const storeName =
      catalogVendeur?.nom_boutique ||
      catalogVendeur?.nom_de_la_boutique ||
      "Nom de la boutique";

    return (
      <div className="template-wrapper">
        <TemplateNavbar vendeurName={storeName} />
        <div className="main-content">
          <h2 className="section-title catalog-products-title">
            Résultats de recherche
          </h2>
          {!!q && (
            <p className="search-results-query text-center">pour « {q} »</p>
          )}
          {resultsMeta}

          <div className="template-products-col">
            <div className="products-grid">
              {(isLoading || (isFetching && allProducts.length === 0)) ? (
                <CatalogProductSkeleton />
              ) : allProducts.length > 0 ? (
                allProducts.map((product, index) => (
                  <ProductCard
                    key={
                      product?.id != null
                        ? `search-product-${product.id}-${index}`
                        : `search-product-fallback-${index}`
                    }
                    item={product}
                    devise={catalogVendeur?.devise}
                    onCardClick={handleCatalogCardClick}
                  />
                ))
              ) : null}
            </div>

            {!isFetching && allProducts.length === 0 && (
              <p className="empty-message text-center">
                Aucun produit trouvé pour cette recherche.
              </p>
            )}

            {backToAllProductsLink}
            {infiniteScrollBlock}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="landing-page-component">
      <TemplateNavbar />
      <div className="page-content">
        <div className="container-easy-market">
          <div className="row row-achat-page mb-5">
            <FilterItems
              searchParams={searchParams}
              setSearchParams={setSearchParams}
              query={query}
              setQuery={setQuery}
              setPage={setPage}
              minAvailablePrice={priceExtremes?.min}
              maxAvailablePrice={priceExtremes?.max}
            />
            <div className="container-filter-parcours-achat-mobile no-view-desktop">
              <button
                className="btn btn-filter-mobile"
                type="button"
                onClick={() => setShow(true)}
              >
                Filtrer
              </button>
            </div>
            <div className="col-lg-9 col-md-7 col-right-achat-page">
              <div className="content-col-right-achat-page">
                <div className="container-home-page mb-3 our-product-container-homepage our-product-grid-3 best-sellers-section">
                  <div className="best-sellers-section-container mt-3">
                    <div className="best-sellers-section-header">
                      <h3 className="title-home-page-theme text-center">
                        Résultats de recherche
                      </h3>

                      {!!q && (
                        <p className="search-results-query text-center">
                          pour « {q} »
                        </p>
                      )}
                      {resultsMeta}
                    </div>

                    <div className="best-sellers-section-grid our-product-grid">
                      {(isLoading || (isFetching && allProducts.length === 0)) && (
                        <ProductItemsSkeleton />
                      )}
                      {allProducts.length > 0 &&
                        allProducts.map((product, index) => (
                          <div
                            key={
                              product?.id != null
                                ? `search-product-${product.id}-${index}`
                                : `search-product-fallback-${index}`
                            }
                            className="best-sellers-section-grid-item"
                          >
                            <BestSellerCard item={product} />
                          </div>
                        ))}
                    </div>

                    {!isFetching && allProducts.length === 0 && (
                      <div className="col-sm-12 col-md-12 mb-3 m-t-36 d-flex justify-content-center">
                        <p>Aucun produit trouvé pour cette recherche.</p>
                      </div>
                    )}

                    {backToAllProductsLink}
                    {infiniteScrollBlock}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <FilterMobileItem
        show={show}
        setShow={setShow}
        searchParams={searchParams}
        setSearchParams={setSearchParams}
        query={query}
        setQuery={setQuery}
        setPage={setPage}
      />
    </div>
  );
}

export default SearchResultsPage;
